import {
	ArrowDown01Icon,
	Home01Icon,
	LayersIcon,
	Logout01Icon,
	Notification03Icon,
	Settings01Icon,
	UserCircleIcon,
	UserIcon,
	UserMultipleIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { Link, router } from "@inertiajs/react";
import type * as React from "react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarTrigger,
} from "@/components/ui/sidebar";
import type HomeUser from "@/types/serializers/HomeUser";

interface NavItem {
	title: string;
	url: string;
	icon?: IconSvgElement;
}

interface DashboardProps {
	user: HomeUser;
	children?: React.ReactNode;
}

export default function Dashboard({ user, children }: DashboardProps) {
	return (
		<SidebarProvider
			style={
				{
					"--sidebar-width": "calc(var(--spacing) * 56)",
					"--header-height": "calc(var(--spacing) * 12)",
				} as React.CSSProperties
			}
		>
			<AppSidebar user={user} />
			<SidebarInset>
				<SiteHeader />
				<div className="flex flex-1 flex-col overflow-hidden">
					<div className="flex flex-1 flex-col gap-4 overflow-hidden">
						{children}
					</div>
				</div>
			</SidebarInset>
		</SidebarProvider>
	);
}

function AppSidebar({ user }: { user: HomeUser }) {
	const navMain: NavItem[] = [
		{
			title: "Dashboard",
			url: "/",
			icon: Home01Icon,
		},
		{
			title: "Klienti",
			url: "/clients",
			icon: UserMultipleIcon,
		},
		{
			title: "Osoby",
			url: "/physical_persons",
			icon: UserIcon,
		},
	];

	const handleLogout = () => {
		router.delete("/sessions/current");
	};

	return (
		<Sidebar collapsible="icon">
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<DropdownMenu>
							<DropdownMenuTrigger
								render={
									<SidebarMenuButton className="data-[state=open]:bg-sidebar-accent data-[slot=sidebar-menu-button]:p-1.5! data-[state=open]:text-sidebar-accent-foreground" />
								}
							>
								<HugeiconsIcon
									icon={LayersIcon}
									strokeWidth={2}
									className="size-5!"
								/>
								<span className="truncate font-semibold text-base">
									{user.company_name}
								</span>
								<HugeiconsIcon
									icon={ArrowDown01Icon}
									strokeWidth={2}
									className="ml-auto size-4"
								/>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
								side="bottom"
								align="start"
								sideOffset={4}
							>
								<DropdownMenuGroup>
									<DropdownMenuLabel className="text-muted-foreground text-xs">
										{user.email}
									</DropdownMenuLabel>
								</DropdownMenuGroup>
								<DropdownMenuSeparator />
								<DropdownMenuItem render={<a href="/sessions" />}>
									<HugeiconsIcon icon={UserCircleIcon} strokeWidth={2} />
									Účet
								</DropdownMenuItem>
								<DropdownMenuItem render={<a href="/password/edit" />}>
									<HugeiconsIcon icon={Settings01Icon} strokeWidth={2} />
									Změna hesla
								</DropdownMenuItem>
								<DropdownMenuItem
									render={<a href="/notification_preferences/edit" />}
								>
									<HugeiconsIcon icon={Notification03Icon} strokeWidth={2} />
									Upozornění
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={handleLogout}>
									<HugeiconsIcon icon={Logout01Icon} strokeWidth={2} />
									Odhlásit se
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				{navMain.length > 0 && (
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								{navMain.map((item) => (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton render={<Link href={item.url} />}>
											{item.icon && (
												<HugeiconsIcon
													icon={item.icon}
													strokeWidth={2}
													className="size-4"
												/>
											)}
											<span>{item.title}</span>
										</SidebarMenuButton>
									</SidebarMenuItem>
								))}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				)}
			</SidebarContent>
		</Sidebar>
	);
}

function SiteHeader() {
	return (
		<header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
			<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
				<SidebarTrigger className="-ml-1" />
				<Separator
					orientation="vertical"
					className="mx-2 data-[orientation=vertical]:h-4"
				/>
				<h1 className="font-medium text-base">Dashboard</h1>
			</div>
		</header>
	);
}
